import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Group,
  Text,
  Table,
  Badge,
  ScrollArea,
  Button,
  Select,
} from '@mantine/core';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

// Array fields are mapped as "AdjLines.DebitAmount[2]" but stored nested
// (AdjLines: [{...}, {...}]) by transformRowToFormData; index is 1-based, default first item.
const fromArrayField = (obj: Record<string, any>, fieldName: string) => {
  const index = Math.max(Number(fieldName.match(/\[(\d+)\]/)?.[1] ?? 1) - 1, 0);
  const path = fieldName.replace(/\[\d+\]/g, '');
  const dot = path.lastIndexOf('.');
  if (dot === -1) return undefined;
  const arr = obj?.[path.slice(0, dot)];
  return Array.isArray(arr) ? arr[index]?.[path.slice(dot + 1)] : undefined;
};

interface BulkDataPreviewProps {
  data: Record<string, any>[];
  fieldMappings?: Record<string, any>;
  fieldModes?: Record<string, 'column' | 'default'>;
  title?: string;
  maxHeight?: number;
  showRowLimit?: boolean;
}

export const BulkDataPreview: React.FC<BulkDataPreviewProps> = ({
  data,
  fieldMappings = {},
  fieldModes = {},
  title = "Data Preview",
  maxHeight = 400,
  showRowLimit = true,
}) => {
  const [rowsToShow, setRowsToShow] = useState<number>(10);
  const [showAllFields, setShowAllFields] = useState(false);

  const previewData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const rowsToDisplay = showRowLimit ? data.slice(0, rowsToShow) : data;
    return rowsToDisplay.map((row, index) => ({
      rowIndex: index + 1,
      data: row,
    }));
  }, [data, rowsToShow, showRowLimit]);

  const fieldNames = useMemo(() => {
    if (Object.keys(fieldMappings).length > 0) {
      return Object.keys(fieldMappings);
    }
    
    // If no field mappings, get all field names from the first row
    if (data && data.length > 0) {
      return Object.keys(data[0]);
    }
    
    return [];
  }, [fieldMappings, data]);

  const visibleFields = useMemo(() => {
    if (showAllFields) {
      return fieldNames;
    }
    // Show first 8 fields by default for better horizontal scrolling
    return fieldNames.slice(0, 8);
  }, [fieldNames, showAllFields]);

  const rowOptions = [
    { value: '3', label: 'First 3 rows' },
    { value: '10', label: 'First 10 rows' },
    { value: '25', label: 'First 25 rows' },
    { value: '50', label: 'First 50 rows' },
    { value: 'all', label: 'All rows' },
  ];

  if (!data || data.length === 0) {
    return (
      <Card withBorder>
        <Text fw={500} mb="sm">{title}</Text>
        <Text c="dimmed" ta="center" py="xl">
          No data to preview
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Box>
          <Text fw={500}>{title}</Text>
          <Text size="sm" c="dimmed">
            Showing {previewData.length} of {data.length} rows • {fieldNames.length} fields
          </Text>
        </Box>
        
        <Group gap="sm">
          {fieldNames.length > 8 && (
            <Button
              variant="light"
              size="xs"
              leftSection={showAllFields ? <IconEyeOff size={14} /> : <IconEye size={14} />}
              onClick={() => setShowAllFields(!showAllFields)}
            >
              {showAllFields ? 'Show Less' : `Show All (${fieldNames.length})`}
            </Button>
          )}
          
          {showRowLimit && (
            <Select
              size="xs"
              data={rowOptions}
              value={rowsToShow === data.length ? 'all' : String(rowsToShow)}
              onChange={(value) => {
                if (value === 'all') {
                  setRowsToShow(data.length);
                } else {
                  setRowsToShow(parseInt(value || '10', 10));
                }
              }}
              style={{ width: 140 }}
            />
          )}
        </Group>
      </Group>

      <ScrollArea h={maxHeight}>
        <ScrollArea.Autosize maw="100%" type="scroll">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 60 }}>Row</Table.Th>
                {visibleFields.map((fieldName) => {
                  const mode = fieldModes[fieldName];
                  return (
                    <Table.Th key={fieldName} style={{ minWidth: 150 }}>
                      <Group gap="xs">
                        <Text size="sm" fw={500}>{fieldName}</Text>
                        {mode === 'default' && (
                          <Badge size="xs" color="blue" variant="light">Default</Badge>
                        )}
                      </Group>
                    </Table.Th>
                  );
                })}
                {!showAllFields && fieldNames.length > 8 && (
                  <Table.Th style={{ minWidth: 100 }}>
                    <Text size="sm" c="dimmed">
                      +{fieldNames.length - 8} more...
                    </Text>
                  </Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {previewData.map((row) => (
                <Table.Tr key={row.rowIndex}>
                  <Table.Td>
                    <Badge size="sm" variant="outline">{row.rowIndex}</Badge>
                  </Table.Td>
                  {visibleFields.map((fieldName) => {
                    // Handle nested field access for multi-record structures
                    let value = row.data[fieldName] ?? fromArrayField(row.data, fieldName);

                    // If direct access fails, try to find the value in nested structures
                    if (value === undefined || value === null) {
                      // Check for nested structures like MultiRecord_fx-transfer__SpotFX
                      for (const key of Object.keys(row.data)) {
                        if (typeof row.data[key] === 'object' && row.data[key] !== null) {
                          // Try to find the field without the prefix
                          const fieldParts = fieldName.split('.');
                          const lastPart = fieldParts[fieldParts.length - 1];
                          
                          // Check if this nested object contains our field
                          const fromArray = fromArrayField(row.data[key], fieldName);
                          if (fromArray !== undefined) {
                            value = fromArray;
                            break;
                          }
                          if (row.data[key][fieldName] !== undefined) {
                            value = row.data[key][fieldName];
                            break;
                          }
                          // Also check with just the last part of the field name
                          if (row.data[key][lastPart] !== undefined) {
                            value = row.data[key][lastPart];
                            break;
                          }
                          
                          // Check if the key ends with the record type prefix (e.g., __SpotFX)
                          const recordPrefix = fieldParts[0]; // e.g., "SpotFX" from "SpotFX.Comments"
                          if (key.endsWith(`__${recordPrefix}`)) {
                            const nestedFieldName = fieldParts.slice(1).join('.') || lastPart;
                            if (row.data[key][nestedFieldName] !== undefined) {
                              value = row.data[key][nestedFieldName];
                              break;
                            }
                          }
                        }
                      }
                    }
                    
                    return (
                      <Table.Td key={fieldName}>
                        <Text size="sm" truncate style={{ maxWidth: 200 }}>
                          {value !== null && value !== undefined 
                            ? String(value) 
                            : '—'
                          }
                        </Text>
                      </Table.Td>
                    );
                  })}
                  {!showAllFields && fieldNames.length > 8 && (
                    <Table.Td>
                      <Text size="sm" c="dimmed">...</Text>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      </ScrollArea>
      
      {previewData.length < data.length && (
        <Text size="xs" c="dimmed" mt="xs" ta="center">
          ... and {data.length - previewData.length} more rows
        </Text>
      )}
    </Card>
  );
};